import React, { useCallback, useRef, useState } from 'react';
import cn from 'classnames';

import { forwardInputRef, IInputProps } from '../types/input';
import { IListInputAttrs, IListInputProps, isSubList, ListInput, TListItems } from '../ListInput';
import { InputBase } from '../InputBase';
import { IDropdownProps, SimpleDropdown } from '../SimpleDropdown';
import { useMergedRef } from '../utils/useMergedRef';
import { Icon } from '../Icon';

import s from './index.module.css';

interface ISelectInputProps<TName extends string = string, TValue extends string = string>
  extends IInputProps<TName, TValue, HTMLInputElement> {
  items: TListItems;
  isExpandedInitially?: boolean;
  Dropdown?: React.FC<IDropdownProps>;
  dataTestId?: string;
  isClearable?: boolean;
}

const findTitle = (items: TListItems, value: string): string | undefined => {
  for (const item of items) {
    if (isSubList(item)) {
      const found = findTitle(item.items, value);

      if (found) {
        return found;
      }
    } else {
      if (item.value === value) {
        return item.title;
      }
    }
  }

  return undefined;
};

const RightIcon = ({
  showClearButton,
  handleRemove,
  isExpanded,
  isDisabled,
}: {
  showClearButton: boolean;
  handleRemove: () => void;
  isExpanded: boolean;
  isDisabled?: boolean;
}) => {
  if (showClearButton) {
    if (isDisabled) {
      return null;
    }

    return (
      <button onClick={handleRemove} className={s.removeButton}>
        <Icon size="14px" icon={'x-circle-filled'} className={s.removeIcon} />
      </button>
    );
  }

  return (
    <Icon
      size="14px"
      icon={'chevron-to-end'}
      className={cn(s.arrow, { [s.isRotated]: isExpanded })}
    />
  );
};

const SelectInput = forwardInputRef(
  <TName extends string = string, TValue extends string = string>(
    props: ISelectInputProps<TName, TValue>,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const {
      value: inputValue,
      items,
      name,
      onChange,
      hasError,
      isDisabled,
      placeholder,
      isExpandedInitially = false,
      Dropdown = SimpleDropdown,
      dataTestId = 'select',
      isClearable = false,
    } = props;

    const showClearButton = Boolean(isClearable && inputValue);

    const inputMutableRef = useRef<HTMLInputElement>(null);
    const mergedRef = useMergedRef<HTMLInputElement>(ref, inputMutableRef);

    const [isExpanded, setIsExpanded] = useState(isExpandedInitially);
    const setIsCollapsed = useCallback(() => {
      inputMutableRef.current?.focus();
      setIsExpanded(false);
    }, [inputMutableRef]);

    const handleClick = useCallback(() => {
      if (showClearButton || isDisabled) {
        return;
      }

      setIsExpanded(!isExpanded);
    }, [isDisabled, isExpanded, showClearButton]);

    const handleChange = useCallback<IListInputProps['onChange']>(
      (event, { value }) => {
        onChange(event, { name, value: value as TValue });
        setIsCollapsed();
      },
      [name, onChange, setIsCollapsed],
    );

    const handleRemove = () => {
      onChange(undefined, { name, value: '' as TValue });
    };

    const listRef = useRef<IListInputAttrs>(null);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (
          (isExpanded && event.key === 'ArrowUp') ||
          event.key === 'ArrowDown' ||
          event.key === 'Tab'
        ) {
          requestAnimationFrame(() => listRef.current?.focus());
        }
      },
      [isExpanded],
    );

    return (
      <Dropdown
        dataTestId={`${dataTestId}__dropdown`}
        onRequireClose={setIsCollapsed}
        target={
          <InputBase
            dataTestId={`${dataTestId}__input`}
            disabled={isDisabled}
            hasError={hasError}
            hasFocusHighlight={isExpanded}
            value={findTitle(items, inputValue) ?? ''}
            type="button"
            onClick={handleClick}
            ref={mergedRef}
            onKeyDown={handleKeyDown}
            className={cn({ [s.button]: !showClearButton })}
            placeholder={placeholder}
            endElement={
              <RightIcon
                handleRemove={handleRemove}
                showClearButton={showClearButton}
                isDisabled={isDisabled}
                isExpanded={isExpanded}
              />
            }
          />
        }
        targetRef={inputMutableRef}
        isExpanded={isExpanded}
      >
        {isExpanded && (
          <div className={s.dropdownContent} data-test-id={`${dataTestId}__expanded-items`}>
            <ListInput
              items={items}
              onChange={handleChange}
              value={inputValue}
              ref={listRef}
              name={name}
            />
          </div>
        )}
      </Dropdown>
    );
  },
);

export type { ISelectInputProps };
export { SelectInput };
